<template>
  <PageLayout>
    <template #headerTitle>Gestion de la Blacklist</template>

    <BlacklistEditor />

    <!-- HEADER WITH ADD BUTTON -->
    <div class="flex justify-between items-center mb-6">
      <div class="text-gray-600">
        <span class="font-semibold text-slate-800">{{ $stores.blacklist?.state?.list_all?.list?.length || 0 }}</span>
        ID(s) bloqué(s) sur <span class="font-semibold text-primary">{{ activeServiceLabel }}</span>
      </div>
      <button
        class="px-6 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center gap-2"
        @click="$bus.emit($constants.EVENTS.BLACKLIST.ADD.SHOW)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Bloquer un ID
      </button>
    </div>

    <!-- ERROR STATE -->
    <div
      v-if="$stores.blacklist?.state?.list_all?.error"
      class="flex flex-col items-center justify-center gap-4 py-16 text-center"
    >
      <div class="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p class="text-gray-600">{{ $stores.blacklist?.state?.list_all?.error }}</p>
      <button
        class="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm hover:bg-slate-700 transition-all"
        @click="refresh"
      >
        Réessayer
      </button>
    </div>

    <!-- BLACKLIST TABLE -->
    <div
      v-else-if="$stores.blacklist?.state?.list_all?.list?.length"
      class="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
    >
      <!-- Table header -->
      <div class="grid grid-cols-[1fr_1fr_auto] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
        <div>ID Joueur</div>
        <div>Service</div>
        <div>Actions</div>
      </div>

      <!-- Table rows -->
      <div
        v-for="(player, index) in $stores.blacklist?.state?.list_all?.list"
        :key="player.id || index"
        class="grid grid-cols-[1fr_1fr_auto] gap-4 px-6 py-4 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-all"
      >
        <!-- Player ID -->
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <div>
            <div class="font-semibold text-slate-800 font-mono">{{ player.playerId }}</div>
            <div class="text-xs text-slate-400">{{ player.id }}</div>
          </div>
        </div>

        <!-- Service badge -->
        <div>
          <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {{ player.service }}
          </span>
        </div>

        <!-- Unblock button -->
        <div>
          <button
            class="px-4 py-1.5 rounded-lg text-sm font-medium text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 transition-all flex items-center gap-1.5"
            @click="confirmRemove(player)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            Débloquer
          </button>
        </div>
      </div>
    </div>

    <!-- LOADING STATE -->
    <div
      v-else-if="$stores.blacklist?.state?.list_all?.loading"
      class="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
    >
      <div class="grid grid-cols-[1fr_1fr_auto] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200">
        <div class="h-3 bg-slate-200 rounded shimmerTarget w-24"></div>
        <div class="h-3 bg-slate-200 rounded shimmerTarget w-16"></div>
        <div class="h-3 bg-slate-200 rounded shimmerTarget w-16"></div>
      </div>
      <div
        v-for="i in 4"
        :key="i"
        class="grid grid-cols-[1fr_1fr_auto] gap-4 px-6 py-4 items-center border-b border-slate-100 last:border-0 shimmerContainer"
      >
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-slate-200 shimmerTarget flex-shrink-0"></div>
          <div class="space-y-2">
            <div class="h-3 bg-slate-200 rounded shimmerTarget w-32"></div>
            <div class="h-2 bg-slate-200 rounded shimmerTarget w-24"></div>
          </div>
        </div>
        <div class="h-6 bg-slate-200 rounded-full shimmerTarget w-20"></div>
        <div class="h-8 bg-slate-200 rounded-lg shimmerTarget w-24"></div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <div
      v-else
      class="flex flex-col items-center justify-center gap-4 py-16 text-center"
    >
      <div class="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
        <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
      <div>
        <p class="font-semibold text-slate-700 text-lg">Aucun ID bloqué</p>
        <p class="text-slate-400 text-sm mt-1">Tous les joueurs sont autorisés sur <span class="font-medium">{{ activeServiceLabel }}</span>.</p>
      </div>
      <button
        class="px-6 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center gap-2"
        @click="$bus.emit($constants.EVENTS.BLACKLIST.ADD.SHOW)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Bloquer un ID
      </button>
    </div>

    <!-- CONFIRM REMOVE DIALOG -->
    <div
      v-if="playerToRemove"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="playerToRemove = null"
    >
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p class="font-bold text-slate-800">Débloquer cet ID ?</p>
            <p class="text-sm text-slate-500">Cette action autorisera le joueur à effectuer des transactions.</p>
          </div>
        </div>

        <div class="bg-slate-50 rounded-xl p-3 mb-5">
          <div class="text-xs text-slate-400 mb-1">ID Joueur</div>
          <div class="font-mono font-bold text-slate-800">{{ playerToRemove?.playerId }}</div>
          <div class="text-xs text-slate-500 mt-1">Service: {{ playerToRemove?.service }}</div>
        </div>

        <div class="flex gap-3">
          <button
            class="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all"
            @click="playerToRemove = null"
          >
            Annuler
          </button>
          <button
            class="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition-all"
            @click="doRemove"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import BlacklistEditor from './BlacklistEditor.mount.vue';

export default {
  name: 'BlacklistPage',
  components: { BlacklistEditor },

  data() {
    return {
      playerToRemove: null
    };
  },

  computed: {
    activeServiceLabel() {
      // capitalize first letter
      return window.lacasaeBettingServiceName
        ? window.lacasaeBettingServiceName.charAt(0).toUpperCase() + window.lacasaeBettingServiceName.slice(1)
        : 'Service inconnu';
    }
  },

  mounted() {
    this.refresh();
    // Re-refresh after an add or remove so the list stays in sync
    this.$bus.on(this.$constants.EVENTS.BLACKLIST.ADD.SUCCESS, this.refresh);
    this.$bus.on(this.$constants.EVENTS.BLACKLIST.REMOVE.SUCCESS, this.refresh);
  },

  beforeUnmount() {
    this.$bus.off(this.$constants.EVENTS.BLACKLIST.ADD.SUCCESS, this.refresh);
    this.$bus.off(this.$constants.EVENTS.BLACKLIST.REMOVE.SUCCESS, this.refresh);
  },

  methods: {
    refresh() {
      this.$bus.emit(this.$constants.EVENTS.BLACKLIST.LIST_ALL.REFRESH, {
        service: window.lacasaeBettingServiceName
      });
    },

    confirmRemove(player) {
      this.playerToRemove = player;
    },

    doRemove() {
      if (!this.playerToRemove) return;
      this.$bus.emit(this.$constants.EVENTS.BLACKLIST.REMOVE.SUBMIT, {
        playerId: this.playerToRemove.playerId,
        service: this.playerToRemove.service,
        currency: 'xof'
      });
      this.playerToRemove = null;
    }
  }
};
</script>
